import { dateToLocalInputValue, localInputValueToDate } from '@/lib/time-input'
import type { TimeRangeMs } from '@/store/activity-store'

export interface RangeFieldsProps {
  idPrefix: string
  showStart?: boolean
  showEnd?: boolean
  startLabel?: string
  endLabel?: string
  start: Date | null
  end: Date | null
  onStartChange: (date: Date | null) => void
  onEndChange: (date: Date | null) => void
  /** The timeline's current brush/manual selection, if any -- offered as a one-click prefill. */
  selectedRangeMs?: TimeRangeMs | null
}

/**
 * The shared manual-entry half of the range-input UI (master spec: "Enter
 * precise timestamps manually" must work as an independent alternative to
 * timeline-handle selection, not just a fallback). Reused by every
 * range-based edit dialog (crop, remove interval, remove-GPS-only,
 * set-GPS-constant, interpolate-GPS) so prefill-from-selection and manual
 * entry behave identically everywhere.
 */
export function RangeFields({
  idPrefix,
  showStart = true,
  showEnd = true,
  startLabel = 'Start',
  endLabel = 'End',
  start,
  end,
  onStartChange,
  onEndChange,
  selectedRangeMs,
}: RangeFieldsProps) {
  const canPrefill = selectedRangeMs != null

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {showStart && (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${idPrefix}-start`} className="text-foreground text-xs font-medium">
              {startLabel}
            </label>
            <input
              id={`${idPrefix}-start`}
              type="datetime-local"
              step={1}
              value={dateToLocalInputValue(start)}
              onChange={(e) => onStartChange(localInputValueToDate(e.target.value))}
              className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
            />
          </div>
        )}
        {showEnd && (
          <div className="flex flex-col gap-1">
            <label htmlFor={`${idPrefix}-end`} className="text-foreground text-xs font-medium">
              {endLabel}
            </label>
            <input
              id={`${idPrefix}-end`}
              type="datetime-local"
              step={1}
              value={dateToLocalInputValue(end)}
              onChange={(e) => onEndChange(localInputValueToDate(e.target.value))}
              className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
            />
          </div>
        )}
      </div>
      {canPrefill && selectedRangeMs != null && (
        <button
          type="button"
          onClick={() => {
            if (showStart) onStartChange(new Date(selectedRangeMs.start))
            if (showEnd) onEndChange(new Date(selectedRangeMs.end))
          }}
          className="border-border text-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring w-fit rounded-md border px-2 py-1 text-xs outline-none focus-visible:ring-2"
        >
          Use timeline selection ({new Date(selectedRangeMs.start).toLocaleTimeString()} &ndash;{' '}
          {new Date(selectedRangeMs.end).toLocaleTimeString()})
        </button>
      )}
    </div>
  )
}
